import { Permission } from '@aon/aon-tws-common-ui';
import _ from 'lodash';
import React from 'react';
import { AdminCard } from '../components/pages/survey-admin/AdminCard';
import Constants from '../constants';
import ContentConstants from '../contentConstants';
import {
	AudienceSourceType,
	AudienceStatus,
	AudienceSummaryItemType,
	DistributionType,
	ISummaryItem,
	ISurveyAdminSummary,
	SurveyStatusType,
} from '../models';
import PermissionConstants from '../permissionConstants';
import { ActionCreators as SurveyAdminActionCreators } from '../store/SurveyAdminStore';
import { useAudienceAdminCards } from './hooks/useAudienceAdminCards';
import { useSurveyAdminCards } from './hooks/useSurveyAdminCards';

export enum SurveyAdminCardType {
	AddRemoveBenchmarks,
	AnonymityThreshold,
	CancelSurvey,
	PreviewSurvey,
	ReportingProfiles,
	ReportingUsers,
	SurveyOverview,
	TrendTo,
	ManageDimensions,
	ReopenSurvey,
}
export enum AudienceAdminCardType {
	CancelEvent,
	DistributionList,
	HierarchyView,
	InviteEmail,
	OpenClose,
	ParticipationMonitor,
	ReminderEmail,
	ResetReopenResend,
	SurveyUrlsList,
	ReopenSurvey,
}

export const SurveyAdminCards = (props: { surveyId: number; cards: SurveyAdminCardType[]; handleCancelSurveyClick: () => void }) => {
	const {
		hasBeenCancelled,
		hasBeenPublished,
		hasUniqueHrisAudienceDistribution,
		isClosed,
		navigate,
		previewSurvey,
		userFeatureAccessHelper,
	} = useSurveyAdminCards(props.surveyId);

	const elements: JSX.Element[] = [];
	_.forEach(props.cards, (card: SurveyAdminCardType) => {
		switch (card) {
			case SurveyAdminCardType.AddRemoveBenchmarks:
				elements.push(
					SurveyCards.getAddRemoveBenchmarksCard(hasBeenPublished, hasBeenCancelled, navigate(Constants.SUBROUTE_SURVEY_ADMIN_BENCHMARKS)),
				);
				break;
			case SurveyAdminCardType.AnonymityThreshold:
				elements.push(
					SurveyCards.getAnonymityThresholdCard(
						hasBeenPublished,
						hasBeenCancelled,
						navigate(Constants.SUBROUTE_SURVEY_ADMIN_ANONYMITY_THRESHOLD),
					),
				);
				break;
			case SurveyAdminCardType.CancelSurvey:
				elements.push(SurveyCards.getCancelSurveyCard(hasBeenPublished, hasBeenCancelled, isClosed, props.handleCancelSurveyClick));
				break;
			case SurveyAdminCardType.PreviewSurvey:
				elements.push(SurveyCards.getPreviewSurveyCard(hasBeenPublished, hasBeenCancelled, previewSurvey));
				break;
			case SurveyAdminCardType.ReportingProfiles:
				{
					const showReportingProfilesCard =
						userFeatureAccessHelper.CanUserAccessFeature('EnableReportingProfiles') && hasUniqueHrisAudienceDistribution;
					elements.push(
						SurveyCards.getReportingProfilesCard(
							showReportingProfilesCard,
							hasBeenPublished,
							hasBeenCancelled,
							navigate(Constants.SUBROUTE_SURVEY_ADMIN_REPORTING_PROFILES),
						),
					);
				}
				break;
			case SurveyAdminCardType.ReportingUsers:
				elements.push(
					SurveyCards.getReportingUsersCard(hasBeenPublished, hasBeenCancelled, navigate(Constants.SUBROUTE_SURVEY_ADMIN_REPORTING_ACCESS)),
				);
				break;
			case SurveyAdminCardType.SurveyOverview:
				elements.push(
					SurveyCards.getSurveyOverviewCard(hasBeenPublished, hasBeenCancelled, navigate(Constants.SUBROUTE_SURVEY_ADMIN_SURVEY_OVERVIEW)),
				);
				break;
			case SurveyAdminCardType.TrendTo:
				{
					const showTrendToCard =
						!userFeatureAccessHelper.CanUserAccessFeature('EnableReportingBulkUsers') || !hasUniqueHrisAudienceDistribution;
					elements.push(
						SurveyCards.getTrendToCard(
							showTrendToCard,
							hasBeenPublished,
							hasBeenCancelled,
							navigate(Constants.SUBROUTE_SURVEY_ADMIN_TREND_TO),
						),
					);
				}
				break;
			case SurveyAdminCardType.ManageDimensions:
				elements.push(SurveyCards.getManageDimensionsCard(navigate(Constants.SUBROUTE_SURVEY_ADMIN_MANAGE_DIMENSIONS)));
				break;
			case SurveyAdminCardType.ReopenSurvey:
				elements.push(SurveyCards.getReopenSurveyCard(isClosed, hasBeenCancelled, navigate(Constants.SUBROUTE_SURVEY_ADMIN_REOPEN_SURVEY)));
				break;
		}
	});

	return <>{elements}</>;
};

export const AudienceAdminCards = (props: {
	surveyId: number;
	audienceId: number;
	cards: AudienceAdminCardType[];
	handleCancelEventClick: () => void;
}) => {
	const {
		audienceSourceType,
		audienceStatus,
		dispatch,
		distributionType,
		hasBeenCancelled,
		hasBeenPublished,
		isClosed,
		navigate,
		numAudiences,
		surveyAdminSummary,
	} = useAudienceAdminCards(props.surveyId, props.audienceId);

	const elements: JSX.Element[] = [];
	_.forEach(props.cards, (card: AudienceAdminCardType) => {
		switch (card) {
			case AudienceAdminCardType.CancelEvent:
				elements.push(AudienceCards.getCancelEventCard(numAudiences, isClosed, hasBeenCancelled, props.handleCancelEventClick));
				break;
			case AudienceAdminCardType.DistributionList:
				elements.push(
					AudienceCards.getDistributionListCard(
						hasBeenPublished,
						hasBeenCancelled,
						distributionType,
						navigate(Constants.SUBROUTE_SURVEY_ADMIN_PARTICIPANTS),
					),
				);
				break;
			case AudienceAdminCardType.HierarchyView:
				elements.push(
					AudienceCards.getHierarchyViewCard(
						hasBeenPublished,
						hasBeenCancelled,
						distributionType,
						audienceSourceType,
						navigate(Constants.SUBROUTE_SURVEY_ADMIN_HIERARCHY_VIEW),
					),
				);
				break;
			case AudienceAdminCardType.InviteEmail:
				elements.push(
					AudienceCards.getInviteEmailCard(
						hasBeenPublished,
						hasBeenCancelled,
						isClosed,
						distributionType,
						navigate(Constants.SUBROUTE_SURVEY_ADMIN_INVITE_EMAIL),
					),
				);
				break;
			case AudienceAdminCardType.OpenClose:
				elements.push(
					AudienceCards.getOpenCloseCard(
						hasBeenPublished,
						hasBeenCancelled,
						isClosed,
						navigate(Constants.SUBROUTE_SURVEY_ADMIN_OPEN_CLOSE),
					),
				);
				break;
			case AudienceAdminCardType.ParticipationMonitor:
				elements.push(
					AudienceCards.getParticipationMonitorCard(
						props.audienceId,
						hasBeenCancelled,
						surveyAdminSummary,
						navigate(Constants.SUBROUTE_SURVEY_ADMIN_PARTICIPATION_MONITOR),
					),
				);
				break;
			case AudienceAdminCardType.ReminderEmail:
				elements.push(
					AudienceCards.getReminderEmailCard(
						hasBeenPublished,
						hasBeenCancelled,
						isClosed,
						distributionType,
						navigate(Constants.SUBROUTE_SURVEY_ADMIN_REMINDER_EMAIL),
					),
				);
				break;
			case AudienceAdminCardType.ResetReopenResend:
				elements.push(
					AudienceCards.getResetReopenResendCard(
						hasBeenPublished,
						hasBeenCancelled,
						isClosed,
						distributionType,
						navigate(Constants.SUBROUTE_SURVEY_ADMIN_RESET_REOPEN_RESEND),
					),
				);
				break;
			case AudienceAdminCardType.SurveyUrlsList:
				elements.push(
					AudienceCards.getSurveyUrlsListCard(audienceStatus, hasBeenPublished, hasBeenCancelled, distributionType, () =>
						dispatch(SurveyAdminActionCreators.DownloadSurveyURLs(props.surveyId)),
					),
				);
				break;
			case AudienceAdminCardType.ReopenSurvey:
				elements.push(
					AudienceCards.getReopenSurvey(isClosed ?? false, hasBeenCancelled, navigate(Constants.SUBROUTE_SURVEY_ADMIN_REOPEN_SURVEY)),
				);
				break;
		}
	});

	return <>{elements}</>;
};

const getCard = (key: number, headline: string, disabled: boolean, onClick: () => void, hide?: boolean) => {
	return <AdminCard attrKey={key} headline={headline} onClick={onClick} disabled={disabled} hide={hide} />;
};

const SurveyCards = {
	getAddRemoveBenchmarksCard: (hasBeenPublished: boolean, hasBeenCancelled: boolean, onClick: () => void) => {
		const disabled = !hasBeenPublished && !hasBeenCancelled;
		return getCard(
			SurveyAdminCardType.AddRemoveBenchmarks,
			ContentConstants.SURVEY_ADMIN_ACTION_ADD_SUBTRACT_BENCHMARKS,
			disabled,
			onClick,
		);
	},

	getAnonymityThresholdCard: (hasBeenPublished: boolean, hasBeenCancelled: boolean, onClick: () => void) => {
		const disabled = !hasBeenPublished && !hasBeenCancelled;
		return getCard(SurveyAdminCardType.AnonymityThreshold, ContentConstants.SURVEY_ADMIN_ACTION_ANONYMITY_THRESHOLD, disabled, onClick);
	},

	getCancelSurveyCard: (hasBeenPublished: boolean, hasBeenCancelled: boolean, isClosed: boolean | undefined, onClick: () => void) => {
		const disabled = !hasBeenPublished || (isClosed as boolean);
		return getCard(
			SurveyAdminCardType.CancelSurvey,
			ContentConstants.SURVEY_ADMIN_ACTION_CANCEL_SURVEY,
			disabled,
			onClick,
			hasBeenCancelled,
		);
	},

	getPreviewSurveyCard: (hasBeenPublished: boolean, hasBeenCancelled: boolean, previewSurvey: () => void) => {
		const disabled = !hasBeenPublished || hasBeenCancelled;
		return getCard(SurveyAdminCardType.PreviewSurvey, ContentConstants.SURVEY_ADMIN_ACTION_PREVIEW_SURVEY, disabled, previewSurvey);
	},

	getReportingProfilesCard: (
		showReportingProfilesCard: boolean,
		hasBeenPublished: boolean,
		hasBeenCancelled: boolean,
		onClick: () => void,
	) => {
		const disabled = !hasBeenPublished && !hasBeenCancelled;
		return (
			<Permission {...PermissionConstants.HIERARCHYREPORTING_ALL}>
				{getCard(
					SurveyAdminCardType.ReportingProfiles,
					ContentConstants.SURVEY_ADMIN_ACTION_REPORTING_PROFILES,
					disabled,
					onClick,
					!showReportingProfilesCard,
				)}
			</Permission>
		);
	},

	getReportingUsersCard: (hasBeenPublished: boolean, hasBeenCancelled: boolean, onClick: () => void) => {
		const disabled = !hasBeenPublished && !hasBeenCancelled;
		return getCard(SurveyAdminCardType.ReportingUsers, ContentConstants.SURVEY_ADMIN_ACTION_REPORTING_USERS, disabled, onClick);
	},

	getSurveyOverviewCard: (hasBeenPublished: boolean, hasBeenCancelled: boolean, onClick: () => void) => {
		const disabled = !hasBeenPublished && !hasBeenCancelled;
		return getCard(SurveyAdminCardType.SurveyOverview, ContentConstants.SURVEY_ADMIN_ACTION_SURVEY_OVERVIEW, disabled, onClick);
	},

	getTrendToCard: (showTrendToCard: boolean, hasBeenPublished: boolean, hasBeenCancelled: boolean, onClick: () => void) => {
		const disabled = !hasBeenPublished && !hasBeenCancelled;
		return getCard(SurveyAdminCardType.TrendTo, ContentConstants.SURVEY_ADMIN_ACTION_TREND_TO, disabled, onClick, !showTrendToCard);
	},

	getManageDimensionsCard: (onClick: () => void) => {
		return getCard(SurveyAdminCardType.ManageDimensions, ContentConstants.SURVEY_ADMIN_ACTION_MANAGE_DIMENSIONS, false, onClick);
	},

	getReopenSurveyCard: (isClosed: boolean, hasBeenCancelled: boolean, onClick: () => void) => {
		return getCard(
			SurveyAdminCardType.ReopenSurvey,
			ContentConstants.SURVEY_ADMIN_ACTION_REOPEN_SURVEY,
			!isClosed || hasBeenCancelled,
			onClick,
		);
	},
};

const AudienceCards = {
	getOpenCloseCard: (hasBeenPublished: boolean, hasBeenCancelled: boolean, isClosed: boolean | undefined, onClick: () => void) => {
		const disabled = !hasBeenPublished || isClosed || hasBeenCancelled;
		return getCard(AudienceAdminCardType.OpenClose, ContentConstants.SURVEY_ADMIN_ACTION_CHANGE_OPEN_CLOSE_DATES, disabled, onClick);
	},

	getParticipationMonitorCard: (
		audienceId: number,
		hasBeenCancelled: boolean,
		surveyAdminSummary: ISurveyAdminSummary | undefined,
		onClick: () => void,
	) => {
		const allAudiencesItems: [] | undefined = surveyAdminSummary?.audienceSummaryItem?.value;

		const audienceSummaryItems: ISummaryItem[] | undefined = _.find(allAudiencesItems, (summaryItem, index: string) => {
			if (index === audienceId.toString()) {
				return true;
			}
		}) as ISummaryItem[] | undefined;

		let isTestAudience = false;
		audienceSummaryItems?.forEach((item: ISummaryItem) => {
			if (item.propertyName == AudienceSummaryItemType.IsTestAudience) {
				isTestAudience = item.value;
			}
		});

		const shouldEnableParticipationMonitor = surveyAdminSummary
			? surveyAdminSummary.hasBeenPublished &&
			  surveyAdminSummary.surveyStatus !== SurveyStatusType.Draft &&
			  surveyAdminSummary.surveyStatus !== SurveyStatusType.Scheduled &&
			  surveyAdminSummary.surveyStatus !== SurveyStatusType.SchedulingInProgress
			: false;

		const disabled = !shouldEnableParticipationMonitor || hasBeenCancelled || isTestAudience;
		return getCard(
			AudienceAdminCardType.ParticipationMonitor,
			ContentConstants.SURVEY_ADMIN_ACTION_PARTICIPATION_MONITOR,
			disabled,
			onClick,
		);
	},

	getInviteEmailCard: (
		hasBeenPublished: boolean,
		hasBeenCancelled: boolean,
		isClosed: boolean | undefined,
		distributionType: DistributionType | undefined,
		onClick: () => void,
	) => {
		const hide =
			distributionType === DistributionType.SingleLinkWithoutDistribution || distributionType === DistributionType.GroupBasedLinks;
		const disabled = (!hasBeenPublished && !hasBeenCancelled) || ((isClosed && !hasBeenCancelled) as boolean);
		return getCard(AudienceAdminCardType.InviteEmail, ContentConstants.SURVEY_ADMIN_ACTION_INVITE_EMAIL, disabled, onClick, hide);
	},

	getReminderEmailCard: (
		hasBeenPublished: boolean,
		hasBeenCancelled: boolean,
		isClosed: boolean | undefined,
		distributionType: DistributionType | undefined,
		onClick: () => void,
	) => {
		const hide =
			distributionType === DistributionType.SingleLinkWithoutDistribution || distributionType === DistributionType.GroupBasedLinks;
		const disabled = (!hasBeenPublished && !hasBeenCancelled) || ((isClosed && !hasBeenCancelled) as boolean);
		return getCard(AudienceAdminCardType.ReminderEmail, ContentConstants.SURVEY_ADMIN_ACTION_REMINDER_EMAILS, disabled, onClick, hide);
	},

	getDistributionListCard: (
		hasBeenPublished: boolean,
		hasBeenCancelled: boolean,
		distributionType: DistributionType | undefined,
		onClick: () => void,
	) => {
		const hide =
			distributionType === DistributionType.SingleLinkWithoutDistribution || distributionType === DistributionType.GroupBasedLinks;
		const disabled = (!hasBeenPublished && !hasBeenCancelled) || (distributionType === undefined && !hasBeenCancelled);
		return getCard(AudienceAdminCardType.DistributionList, ContentConstants.SURVEY_ADMIN_ACTION_DISTRIBUTION_LIST, disabled, onClick, hide);
	},

	getHierarchyViewCard: (
		hasBeenPublished: boolean,
		hasBeenCancelled: boolean,
		distributionType: DistributionType | undefined,
		audienceSourceType: AudienceSourceType | undefined,
		onClick: () => void,
	) => {
		const hide =
			audienceSourceType !== AudienceSourceType.Hris ||
			distributionType === DistributionType.SingleLinkWithoutDistribution ||
			distributionType === DistributionType.GroupBasedLinks;
		const disabled = (!hasBeenPublished && !hasBeenCancelled) || (distributionType === undefined && !hasBeenCancelled);
		return getCard(AudienceAdminCardType.HierarchyView, ContentConstants.SURVEY_ADMIN_ACTION_HIERARCHY_VIEW, disabled, onClick, hide);
	},

	getResetReopenResendCard: (
		hasBeenPublished: boolean,
		hasBeenCancelled: boolean,
		isClosed: boolean | undefined,
		distributionType: DistributionType | undefined,
		onClick: () => void,
	) => {
		const hide =
			distributionType === DistributionType.SingleLinkWithoutDistribution || distributionType === DistributionType.GroupBasedLinks;
		const disabled =
			(!hasBeenPublished && !hasBeenCancelled) ||
			(distributionType === undefined && !hasBeenCancelled) ||
			hasBeenCancelled ||
			(isClosed as boolean);
		return getCard(
			AudienceAdminCardType.ResetReopenResend,
			ContentConstants.SURVEY_ADMIN_ACTION_RESET_REOPEN_RESEND,
			disabled,
			onClick,
			hide,
		);
	},

	getSurveyUrlsListCard: (
		audienceStatus: AudienceStatus | undefined,
		hasBeenPublished: boolean,
		hasBeenCancelled: boolean,
		distributionType: DistributionType | undefined,
		onClick: () => void,
	) => {
		const isGroupBasedLinkReadyToDownload =
			audienceStatus === AudienceStatus.Queued || audienceStatus === AudienceStatus.Live || audienceStatus === AudienceStatus.Closed;
		const show = distributionType === DistributionType.GroupBasedLinks;
		const disabled = hasBeenCancelled || !(hasBeenPublished && isGroupBasedLinkReadyToDownload);
		return getCard(
			AudienceAdminCardType.SurveyUrlsList,
			ContentConstants.SURVEY_ADMIN_ACTION_DOWNLOAD_SURVEY_URLS_LIST,
			disabled,
			onClick,
			!show,
		);
	},

	getCancelEventCard: (numAudiences: number, isClosed: boolean | undefined, hasBeenCancelled: boolean, onClick: () => void) => {
		const show = numAudiences > 1;
		const disabled = isClosed || hasBeenCancelled;
		return getCard(AudienceAdminCardType.CancelEvent, ContentConstants.SURVEY_ADMIN_ACTION_CANCEL_EVENT, disabled ?? true, onClick, !show);
	},

	getReopenSurvey: (isClosed: boolean, hasBeenCanceled: boolean, onClick: () => void) => {
		return getCard(
			AudienceAdminCardType.ReopenSurvey,
			ContentConstants.SURVEY_ADMIN_ACTION_REOPEN_SURVEY,
			!isClosed || hasBeenCanceled,
			onClick,
		);
	},
};

export const AdminHelper = {
	SurveyAdminCards,
	AudienceAdminCards,
};
